/**
 * ImageTool.tsx
 * 
 * 职责：
 * 1. 提供图片工具的UI面板
 * 2. 允许用户上传和添加图片到画布
 * 3. 提供图片属性设置选项
 * 4. 处理图片上传和转换逻辑
 */

import React, { useRef, useState, useEffect } from 'react';
import { ImageService } from '../services/ImageService';

interface ImageToolProps {
  imageService: ImageService;
  isActive: boolean;
  position?: { top: number, left: number };
}

const ImageTool: React.FC<ImageToolProps> = ({ imageService, isActive, position }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [uploadPosition, setUploadPosition] = useState<{ x: number, y: number } | null>(null);
  
  // 监听画布点击事件，用于处理用户在画布上点击时的图片上传
  useEffect(() => {
    const handleCanvasImageUpload = (e: CustomEvent) => {
      // 检查工具是否处于活跃状态
      if (!isActive) return;
      
      // 接收点击位置信息
      const { x, y } = e.detail;
      
      // 保存上传位置
      setUploadPosition({ x, y });
      
      // 触发文件选择对话框
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };
    
    // 添加自定义事件监听器
    document.addEventListener('canvas-image-upload', handleCanvasImageUpload as EventListener);
    
    // 清理函数
    return () => {
      document.removeEventListener('canvas-image-upload', handleCanvasImageUpload as EventListener);
    };
  }, [isActive]);

  // 监听isActive变化，决定是否显示面板
  useEffect(() => {
    if (isActive) {
      // 延迟一帧显示，确保CSS过渡生效
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]); // 只依赖isActive

  // 触发文件选择对话框
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 处理文件选择
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setError(null);
      setUploadProgress(0);

      // 获取图片插入位置 - 如果有用户指定的位置则使用，否则使用视口中心
      let insertX, insertY;
      if (uploadPosition) {
        // 使用用户在画布上点击的位置
        insertX = uploadPosition.x;
        insertY = uploadPosition.y;
      } else {
        // 使用视口中心作为默认位置
        const viewportCenter = imageService.getViewportCenter();
        insertX = viewportCenter.x;
        insertY = viewportCenter.y;
      }

      // 上传图片到指定位置
      const imageData = await imageService.uploadImageFromFile(files[0], insertX, insertY);
      
      if (!imageData) {
        throw new Error('上传图片失败');
      }

      // 重置上传位置，以便下次上传
      setUploadPosition(null);
      
      setUploadProgress(100);

      // 清除文件输入，以便可以再次选择同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // 延迟重置进度条
      setTimeout(() => {
        setUploadProgress(null);
      }, 1000);

      // 触发渲染画布
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 触发工具操作完成事件
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));

    } catch (error) {
      setError(error instanceof Error ? error.message : '上传图片失败');
      setUploadProgress(null);
    }
  };

  // 处理从URL添加图片
  const handleAddFromUrl = async () => {
    const url = prompt('请输入图片URL:');
    if (!url) return;

    try {
      setError(null);
      setUploadProgress(0);

      // 获取图片插入位置 - 如果有用户指定的位置则使用，否则使用视口中心
      let insertX, insertY;
      if (uploadPosition) {
        // 使用用户在画布上点击的位置
        insertX = uploadPosition.x;
        insertY = uploadPosition.y;
      } else {
        // 使用视口中心作为默认位置
        const viewportCenter = imageService.getViewportCenter();
        insertX = viewportCenter.x;
        insertY = viewportCenter.y;
      }

      // 添加图片到指定位置
      const imageData = await imageService.addImage(url, insertX, insertY);
      
      if (!imageData) {
        throw new Error('添加图片失败');
      }

      // 重置上传位置，以便下次上传
      setUploadPosition(null);
      
      setUploadProgress(100);

      // 清除文件输入，以便可以再次选择同一文件
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // 延迟重置进度条
      setTimeout(() => {
        setUploadProgress(null);
      }, 1000);

      // 触发渲染画布
      document.dispatchEvent(new CustomEvent('render-required'));
      
      // 触发工具操作完成事件
      document.dispatchEvent(new CustomEvent('tool-operation-complete'));

    } catch (error) {
      setError(error instanceof Error ? error.message : '添加图片失败');
      setUploadProgress(null);
    }
  };
  
  // 防止事件冒泡，阻止点击工具面板选项时自动切换回选择工具
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };

  // 计算面板位置
  const panelStyle = position ? {
    top: `${position.top}px`,
    left: `${position.left}px`,
  } : {
    top: '60px',
    left: '80px',
  };

  // 如果工具未激活，不显示任何内容
  if (!isActive) return null;
  
  // 隐藏文件输入，只在需要时通过代码触发
  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        style={{ display: 'none' }}
      />
      
      {/* 面板始终渲染，通过CSS控制其可见性 */}
      <div 
        className="image-tool-panel" 
        style={{
          position: 'absolute',
          ...panelStyle,
          backgroundColor: '#f5f5f5',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          zIndex: 9000,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          width: '240px',
          pointerEvents: isVisible ? 'auto' : 'none', // 不可见时禁用鼠标交互
          visibility: isActive ? 'visible' : 'hidden', // 工具不活跃时完全隐藏
        }}
        onClick={handlePanelClick}
      >
        <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>图片工具</h3>
        
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={(e) => { handlePanelClick(e); handleUploadClick(); }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#4a90e2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: 1
            }}
          >
            上传图片
          </button>
          
          <button
            onClick={(e) => { handlePanelClick(e); handleAddFromUrl(); }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#4a90e2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              flex: 1
            }}
          >
            从URL添加
          </button>
        </div>
        
        {uploadProgress !== null && (
          <div style={{ width: '100%', marginTop: '10px' }}>
            <div style={{ height: '5px', backgroundColor: '#e0e0e0', borderRadius: '2px' }}>
              <div
                style={{
                  height: '100%',
                  width: `${uploadProgress}%`,
                  backgroundColor: '#4caf50',
                  borderRadius: '2px',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
            <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '5px' }}>
              {uploadProgress === 100 ? '上传完成!' : `上传中... ${uploadProgress}%`}
            </div>
          </div>
        )}
        
        {error && (
          <div style={{ color: '#e53935', fontSize: '12px', marginTop: '5px' }}>
            错误: {error}
          </div>
        )}
        
        <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
          提示: 上传图片后，可以在画布上拖动和调整图片大小。
        </div>
      </div>
    </>
  );
};

export default ImageTool; 